import { X } from "lucide-react";
import { useState } from "react";
import dayjs from "dayjs";
import { isPrerender, useAfterHydration } from "src/common/prerender";

// 닫은 "날짜"를 저장하는 localStorage 키
const STORAGE_KEY = "isms-banner-dismissed-date";

// 배너 안내 문구 (이미지 대체 텍스트로 사용)
const BANNER_ALT =
  "대한민국 로펌 최초, ISMS-P 인증 · 국가공인 최고등급 보안인증 법률서비스";

/**
 * 랜딩페이지 헤더 위에 붙는 띠배너 (ISMS-P 인증 안내).
 * - 모바일: '닫기'까지 포함된 이미지(mobile-top-banner.webp)를 풀폭으로 표시
 * - 태블릿 이상: 문구+로고 이미지(top-banner.webp)를 가운데 두고 우측에 닫기 버튼
 * "오늘 하루동안 보지 않기"를 누르면 그날은 다시 뜨지 않고, 다음 날 다시 표시된다.
 */
export function TopBanner() {
  // 오늘 날짜 "YYYY-MM-DD" (dayjs는 기기 로컬 시간 기준)
  const today = dayjs().format("YYYY-MM-DD");

  // 처음 렌더할 때 딱 한 번 localStorage를 읽어 표시 여부를 결정 (useEffect 불필요)
  const [storedVisible, setVisible] = useState(
    () => localStorage.getItem(STORAGE_KEY) !== today,
  );
  // 사전 렌더 HTML은 항상 배너가 보이는 상태라, 수화 첫 렌더도 보이게 두고 마운트 뒤에 닫은 기록을 반영한다
  const visible = useAfterHydration(storedVisible, true);
  // "오늘 하루동안 보지 않기" 문구는 사전 렌더 HTML에 넣지 않는다(검색 요약 오염 방지). 버튼이 absolute라 늦게 그려도 밀림 없음
  const showDismissLabel = useAfterHydration(!isPrerender(), false);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, today);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <aside className="w-full bg-[#EDF1FB] border-b border-[#DCE4F5]">
      <div className="relative mx-auto flex items-center justify-center desktop:max-w-[1170px]">
        {/*
          뷰포트에 맞는 이미지 하나만 다운로드 (picture + source)
          - 태블릿 이상(≥768px): 문구+로고 이미지를 원본 비율 그대로 (690×77)
          - 모바일: '닫기'가 포함된 풀폭 이미지, object-cover로 위아래 여백만 잘라 52px로
        */}
        <picture>
          <source
            media="(min-width: 768px)"
            srcSet="https://vws-front-store.s3.ap-northeast-2.amazonaws.com/landing/top-banner.webp"
          />
          <img
            src="https://vws-front-store.s3.ap-northeast-2.amazonaws.com/landing/mobile-top-banner.webp"
            alt={BANNER_ALT}
            className="w-full h-[52px] object-cover tablet:w-auto tablet:h-[57px] desktop:h-[77px] tablet:object-contain"
          />
        </picture>

        {/*
          닫기 버튼 (모바일/데스크톱 통합)
          - 모바일: 이미지 속 '닫기 ✕' 위의 투명 탭 영역(w-24)만, 텍스트·아이콘은 숨김
          - 태블릿 이상: '오늘 하루동안 보지 않기' 텍스트 + X 아이콘 표시
        */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="배너 닫기"
          className="absolute inset-y-0 right-0 w-24 tablet:right-8 tablet:w-auto tablet:flex items-center gap-1 desktop:right-0 text-sm text-[#5A6675] hover:text-[#1F2C44] transition-colors"
        >
          {showDismissLabel && (
            <span className="hidden tablet:inline">오늘 하루동안 보지 않기</span>
          )}
          <X className="hidden tablet:block h-4 w-4" />
        </button>
      </div>
    </aside>
  );
}
